/**
 * Favorites API Service
 *
 * Provides API methods for managing user favorites
 * Uses coreAPI client which automatically handles auth tokens and response unwrapping
 */

import { coreAPI } from './client';

// Entity types that can be favorited
export type FavoriteEntityType = 'product' | 'merchant' | 'service' | 'category';

/**
 * A single favorite item returned from the API
 */
export interface FavoriteItem {
  favorite_id: string;
  entity_type: FavoriteEntityType;
  entity_id: string;
  metadata?: Record<string, unknown>;
  created_at: string;
}

/**
 * Pagination info for favorites list
 */
export interface FavoritesPagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

/**
 * Response from listing favorites
 */
export interface FavoritesListResponse {
  items: FavoriteItem[];
  pagination: FavoritesPagination;
}

/**
 * Parameters for listing favorites
 */
export interface ListFavoritesParams {
  type?: FavoriteEntityType;
  merchant_guid?: string;
  page?: number;
  limit?: number;
}

/**
 * Request to add a favorite
 */
export interface AddFavoriteRequest {
  entity_type: FavoriteEntityType;
  entity_id: string;
  metadata?: Record<string, unknown>;
}

/**
 * Request to batch check favorites
 */
export interface CheckFavoritesRequest {
  entity_type: FavoriteEntityType;
  entity_ids: string[];
}

/**
 * Response from batch check - map of entity_id to boolean
 */
export type CheckFavoritesResponse = Record<string, boolean>;

/**
 * Favorites API methods
 */
export const favoritesAPI = {
  /**
   * List user favorites with optional filtering
   *
   * @param params - Filter and pagination options
   * @returns Paginated list of favorites
   */
  async list(params?: ListFavoritesParams): Promise<FavoritesListResponse> {
    const searchParams = new URLSearchParams();

    if (params?.type) {
      searchParams.set('type', params.type);
    }
    if (params?.merchant_guid) {
      searchParams.set('merchant_guid', params.merchant_guid);
    }
    if (params?.page) {
      searchParams.set('page', String(params.page));
    }
    if (params?.limit) {
      searchParams.set('limit', String(params.limit));
    }

    const query = searchParams.toString();
    return coreAPI.get<FavoritesListResponse>(
      `/users/me/favorites${query ? `?${query}` : ''}`
    );
  },

  /**
   * Add an item to favorites (idempotent)
   *
   * @param data - Entity type, id, and optional metadata
   * @returns Created favorite item
   */
  async add(data: AddFavoriteRequest): Promise<FavoriteItem> {
    return coreAPI.post<FavoriteItem>('/users/me/favorites', data);
  },

  /**
   * Remove an item from favorites
   *
   * @param entityType - Type of entity
   * @param entityId - Entity ID
   * @returns Deletion result
   */
  async remove(
    entityType: FavoriteEntityType,
    entityId: string
  ): Promise<{ deleted: boolean }> {
    return coreAPI.delete<{ deleted: boolean }>(
      `/users/me/favorites/${entityType}/${entityId}`
    );
  },

  /**
   * Batch check if items are favorited
   *
   * @param data - Entity type and array of entity IDs
   * @returns Map of entity_id to boolean
   */
  async check(data: CheckFavoritesRequest): Promise<CheckFavoritesResponse> {
    return coreAPI.post<CheckFavoritesResponse>('/users/me/favorites/check', data);
  },
};
